import React, { useEffect, useState } from "react";
import {
  fetchUpcomingEvents,
  editUpcomingEvent,
  updateUpcomingEvent,
  trashUpcomingEvent,
} from "../../Adapters/ApiAdapter";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseImage_path, baseUrl } from "../../variables/Variables";
import ReactQuill from "react-quill";
import SeoTags from "../SeoTags/SeoTags";
import CKEditorField from "../../components/CKEditorField";

const UpcomingEventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEvent, setEditEvent] = useState(null);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  // const [editImage, setEditImage] = useState(null); // New state for the image

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetchUpcomingEvents();
        setEvents(
          [...(response.data || [])].sort((a, b) => b.id - a.id)
        );
      } catch (err) {
        toast.error("Failed to fetch upcoming events.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editUpcomingEvent(id);
      console.log("Edit Event response:", response.data);
      if (response && response.data) {
        const events = response.data;
        setEditEvent({
          id: events.id,
          name: events.name,
          slug: events.slug,
          event_type: events.event_type,
          start_event_date: events.start_event_date,
          end_event_date: events.end_event_date,
          heading: events.heading,
          description: events.description,
          location: events.location,
          main_img_path: events.main_img_path,
          additional_images: events.additional_images,
          banner_img: events.banner_img,
          seo_title: events.seo_title,
          meta_title: events.meta_title,
          meta_description: events.meta_description,
          meta_keywords: events.meta_keywords,
          custom_scripts: events.custom_scripts,
          og_title: events.og_title,
          og_description: events.og_description,
          og_type: events.og_type,
          og_url: events.og_url,
          og_image: events.og_image,
          og_locale: events.og_locale,
          og_site_name: events.og_site_name,
          og_image_width: events.og_image_width,
          og_image_height: events.og_image_height,
          og_image_type: events.og_image_type,
          article_modified_time: events.article_modified_time,
          canonical_link: events.canonical_link,
          twitter_card: events.twitter_card,
          twitter_site: events.twitter_site,
          twitter_title: events.twitter_title,
          twitter_description: events.twitter_description,
          twitter_image: events.twitter_image,
        });
      } else {
        toast.error("No event data found for editing.");
      }
    } catch (err) {
      toast.error("Failed to load event data for editing.");
    }
  };

  const isValidDate = (d) => {
    return d && !isNaN(new Date(d).getTime());
  };

  const handleSaveEdit = async () => {
    setLoadingAction(true);

    // here we check the end date should not be less than/before the start date
    if (
      new Date(editEvent.end_event_date) < new Date(editEvent.start_event_date)
    ) {
      toast.error("End date cannot be before the start date.");
      setLoadingAction(false);
      return;
    }

    const formData = new FormData();
    // const stripHtml = (html) => html.replace(/<[^>]+>/g, "").trim();

    formData.append("name", editEvent.name);
    formData.append("slug", editEvent.slug);
    formData.append("event_type", editEvent.event_type);
    formData.append("start_event_date", editEvent.start_event_date);
    formData.append("end_event_date", editEvent.end_event_date);
    formData.append("heading", editEvent.heading);
    formData.append("description", editEvent.description); // Strip HTML tags
    formData.append("location", editEvent.location);
    formData.append("seo_title", editEvent.seo_title);
    formData.append("meta_title", editEvent.meta_title);
    formData.append("meta_description", editEvent.meta_description);
    formData.append("meta_keywords", editEvent.meta_keywords);
    formData.append("custom_scripts", editEvent.custom_scripts);
    formData.append("og_title", editEvent.og_title);
    formData.append("og_description", editEvent.og_description);
    formData.append("og_type", editEvent.og_type);
    formData.append("og_url", editEvent.og_url);
    formData.append("og_locale", editEvent.og_locale);
    formData.append("og_site_name", editEvent.og_site_name);
    formData.append("og_image_width", parseInt(editEvent.og_image_width || 0));
    formData.append(
      "og_image_height",
      parseInt(editEvent.og_image_height || 0)
    );
    formData.append("og_image_type", editEvent.og_image_type || "");
    // formData.append("article_modified_time", editEvent.article_modified_time);
    formData.append(
      "article_modified_time",
      isValidDate(editEvent.article_modified_time)
        ? new Date(editEvent.article_modified_time).toISOString().split("T")[0]
        : ""
    );
    formData.append("canonical_link", editEvent.canonical_link);
    formData.append("twitter_card", editEvent.twitter_card);
    formData.append("twitter_site", editEvent.twitter_site);
    formData.append("twitter_title", editEvent.twitter_title);
    formData.append("twitter_description", editEvent.twitter_description);

    if (editEvent.main_img_path instanceof File) {
      formData.append("main_img_path", editEvent.main_img_path);
    }
    if (Array.isArray(editEvent.additional_images)) {
      editEvent.additional_images.forEach((img) => {
        if (img instanceof File) {
          formData.append("additional_images[]", img);
        }
      });
    }
    if (editEvent.banner_img instanceof File) {
      formData.append("banner_img", editEvent.banner_img);
    }
    if (editEvent.og_image instanceof File) {
      formData.append("og_image, editEvent.og_image");
    }
    if (editEvent.twitter_image instanceof File) {
      formData.append("twitter_image", editEvent.twitter_image);
    }

    try {
      await updateUpcomingEvent(editEvent.id, formData);
      toast.success("Event updated successfully!");
      setEvents((prev) =>
        prev.map((event) =>
          event.id === editEvent.id
            ? {
              ...event,
              name: editEvent.name,
              slug: editEvent.slug,
              event_type: editEvent.event_type,
              start_event_date: editEvent.start_event_date,
              end_event_date: editEvent.end_event_date,
              heading: editEvent.heading,
              description: editEvent.description,
              location: editEvent.location,
              main_img_path: editEvent.main_img_path,
              additional_images: editEvent.additional_images,
              banner_img: editEvent.banner_img,
              seo_title: editEvent.seo_title,
              meta_title: editEvent.meta_title,
              meta_description: editEvent.meta_description,
              meta_keywords: editEvent.meta_keywords,
              custom_scripts: editEvent.custom_scripts,
              og_title: editEvent.og_title,
              og_description: editEvent.og_description,
              og_type: editEvent.og_type,
              og_url: editEvent.og_url,
              og_image: editEvent.og_image,
              og_locale: editEvent.og_locale,
              og_site_name: editEvent.og_site_name,
              og_image_width: editEvent.og_image_width,
              og_image_height: editEvent.og_image_height,
              og_image_type: editEvent.og_image_type,
              article_modified_time: editEvent.article_modified_time,
              canonical_link: editEvent.canonical_link,
              twitter_card: editEvent.twitter_card,
              twitter_site: editEvent.twitter_site,
              twitter_title: editEvent.twitter_title,
              twitter_description: editEvent.twitter_description,
              twitter_image: editEvent.twitter_image,
            }
            : event
        )
      );
      setEditEvent(null);
    } catch (err) {
      toast.error("Failed to save changes.");
    } finally {
      setLoadingAction(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&/g, " ")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");
  };

  const handleDelete = async (id) => {
    setLoadingAction(true);
    try {
      const response = await trashUpcomingEvent(id);
      if (response && response.status === "true") {
        toast.success("Event deleted successfully!");
        setEvents((prev) => prev.filter((event) => event.id !== id));
        setDeleteEventId(null);
      } else {
        toast.error("Failed to delete Events");
      }
    } catch (err) {
      toast.error("Failed to delete event.");
    } finally {
      setLoadingAction(false);
    }
  };

  const stripHtml = (html) => (html ? html.replace(/<[^>]+>/g, "").trim() : "");

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "event_type", headerName: "Event Type", width: 150 },
    { field: "start_event_date", headerName: "Start_Event Date", width: 150 },
    { field: "end_event_date", headerName: "End_Event Date", width: 150 },
    { field: "heading", headerName: "Heading", width: 200 },
    { field: "description", headerName: stripHtml("Description"), width: 300 },
    { field: "location", headerName: "Location", width: 150 },
    {
      field: "main_img_path",
      headerName: "Image Path",
      width: 250,
      renderCell: (params) => {
        const imageUrl = params.row.main_img_path
          ? `${baseImage_path}/${params.row.main_img_path}`
          : "/default-banner.jpg"; // Default image if `img_path` is missing

        return (
          <img
            src={imageUrl}
            alt="Event"
            style={{
              width: "100px",
              height: "50px",
              objectFit: "cover",
              borderRadius: "5px",
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteEventId(params.row.id)}
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Upcoming Events
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid rows={events} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editEvent && (
        <Dialog open onClose={() => setEditEvent(null)}>
          <DialogTitle>Edit Upcoming Event</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              value={editEvent.name}
              onChange={(e) => {
                const newTitle = e.target.value;
                setEditEvent({
                  ...editEvent,
                  name: newTitle,
                  slug: generateSlug(newTitle),
                });
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Slug"
              name="slug"
              value={editEvent.slug}
              onChange={(e) =>
                setEditEvent({ ...editEvent, slug: e.target.value })
              }
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="event-type-label">Event Type</InputLabel>
              <Select
                labelId="event-type-label"
                name="event_type"
                value={editEvent.event_type}
                onChange={(e) =>
                  setEditEvent({ ...editEvent, event_type: e.target.value })
                }
              >
                <MenuItem value="CME">CME</MenuItem>
                <MenuItem value="Camps">Camps</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Start_Event Date"
              name="start_event_date"
              type="date"
              value={editEvent.start_event_date}
              onChange={(e) =>
                setEditEvent({ ...editEvent, start_event_date: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="End Event Date"
              name="end_event_date"
              type="date"
              value={editEvent.end_event_date}
              onChange={(e) =>
                setEditEvent({ ...editEvent, end_event_date: e.target.value })
              }
            />

            <TextField
              fullWidth
              margin="normal"
              label="Heading"
              value={editEvent.heading}
              onChange={(e) =>
                setEditEvent({ ...editEvent, heading: e.target.value })
              }
            />

            <Typography variant="body1">Description</Typography>
            {/* <ReactQuill
              theme="snow"
              value={editEvent.description}
              onChange={(event) =>
                setEditEvent({ ...editEvent, description: event })
              }
              style={{ height: "200px", marginBottom: "50px" }}
            /> */}
            <CKEditorField
            value={editEvent.description}
            onChange={(data) => setEditEvent((prev) => ({ ...prev, description: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=events`}
          />

            <TextField
              fullWidth
              margin="normal"
              label="Location"
              value={editEvent.location}
              onChange={(e) =>
                setEditEvent({ ...editEvent, location: e.target.value })
              }
            />

            <SeoTags formData={editEvent} setFormData={setEditEvent} />

            <Box
              mt={2}
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Button
                variant="contained"
                component="label"
                style={{ marginTop: "20px" }}
              >
                Upload Event Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    setEditEvent({
                      ...editEvent,
                      main_img_path:
                        e.target.files.length > 0
                          ? e.target.files[0]
                          : editEvent.main_img_path,
                    })
                  }
                />
              </Button>
              {editEvent.main_img_path && (
                <Box mt={2} mb={2} textAlign="center">
                  <img
                    src={`${baseImage_path}/${editEvent.main_img_path}`}
                    alt="Featured"
                    style={{
                      width: "100px",
                      height: "auto",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
              )}
            </Box>
            {/* --- Additional Images Upload --- */}
            <Box mt={2}>
              <Typography variant="subtitle1">Additional Images</Typography>
              <Button variant="contained" component="label">
                Upload Additional Images
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files).map((file) => {
                      const timestamp = Date.now();
                      return new File([file], `${timestamp}-${file.name}`, {
                        type: file.type,
                      });
                    });
                    setEditEvent((prev) => ({
                      ...prev,
                      additional_images: files,
                    }));
                  }}
                />
              </Button>

              {/* PREVIEW previously uploaded images */}
              {Array.isArray(editEvent.additional_images) &&
                editEvent.additional_images.length > 0 &&
                typeof editEvent.additional_images[0] === "string" && (
                  <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                    {editEvent.additional_images.map((img, i) => (
                      <img
                        key={i}
                        src={`${baseImage_path}/${img}`}
                        alt={`additional-${i}`}
                        style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                      />
                    ))}
                  </Box>
                )}

              {/* PREVIEW for newly uploaded images */}
              {Array.isArray(editEvent.additional_images) &&
                editEvent.additional_images.length > 0 &&
                editEvent.additional_images[0] instanceof File && (
                  <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                    {editEvent.additional_images.map((file, i) => (
                      <img
                        key={i}
                        src={URL.createObjectURL(file)}
                        alt={`new-${i}`}
                        style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                      />
                    ))}
                  </Box>
                )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setEditEvent(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteEventId && (
        <Dialog open onClose={() => setDeleteEventId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this event?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteEventId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteEventId)}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default UpcomingEventsList;
